import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { 
  Phone, 
  Mail, 
  Clock,
  Shield,
  CheckCircle2,
  Users,
  DollarSign,
  Plane,
  Car,
  Star,
  Award,
  Headphones,
  Globe,
  ArrowRight,
  Sparkles,
  Zap,
  MousePointerClick
} from 'lucide-react';
import { TransferBookingForm } from '@/components/TransferBookingForm';
import { TransferSearchResults } from '@/components/TransferSearchResults';
import type { TransferSearchResultData } from '@/components/TransferBookingForm';
import { useTestimonials } from '@/hooks/useTestimonials';
import Layout from '@/components/Layout';
import { useHeroSlides } from '@/hooks/useHeroSlides';
import { useSiteConfig } from '@/hooks/useSiteConfig';
import { useState, useEffect } from 'react';

const HomePage = () => {
  const { testimonials } = useTestimonials();
  const { data: heroSlides, isLoading: slidesLoading } = useHeroSlides();
  const { getConfig } = useSiteConfig();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [searchResults, setSearchResults] = useState<TransferSearchResultData | null>(null);

  const handleResultsFound = (data: TransferSearchResultData) => {
    setSearchResults(data);
    setTimeout(() => {
      document.getElementById('search-results')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };
  
  const featuredTestimonials = testimonials.filter(t => t.is_featured).slice(0, 3);

  useEffect(() => {
    if (heroSlides && heroSlides.length > 0) {
      const timer = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % heroSlides.length);
      }, 6000);
      return () => clearInterval(timer);
    }
  }, [heroSlides]);

  const airports = [
    { code: "PUJ", name: "Punta Cana International Airport", description: "Main gateway to Punta Cana and Bávaro", popular: true, transferTime: "15-45 min" },
    { code: "SDQ", name: "Las Américas International Airport", description: "Serving Santo Domingo and surrounding areas", popular: false, transferTime: "2-3 hours" },
    { code: "LRM", name: "La Romana International Airport", description: "Gateway to La Romana and Casa de Campo", popular: false, transferTime: "45-60 min" },
    { code: "AZS", name: "Samaná El Catey International Airport", description: "Access to Samaná Peninsula", popular: false, transferTime: "2.5-3 hours" },
    { code: "POP", name: "Puerto Plata International Airport", description: "North coast access", popular: false, transferTime: "3.5-4 hours" },
    { code: "STI", name: "Cibao International Airport", description: "Santiago and Cibao region", popular: false, transferTime: "4+ hours" }
  ];

  const steps = [
    { icon: MousePointerClick, title: "Book Online", description: "Select your route, date, and vehicle in just a few clicks", number: "01" },
    { icon: Car, title: "We Pick You Up", description: "Your professional driver meets you with a name sign at the airport", number: "02" },
    { icon: Sparkles, title: "Enjoy Your Ride", description: "Relax in comfort and arrive at your destination stress-free", number: "03" },
  ];

  const features = [
    { icon: Users, title: "Professional Drivers", description: "Bilingual, licensed, and experienced drivers who know Punta Cana inside out." },
    { icon: Car, title: "Modern Fleet", description: "Clean, comfortable, and air-conditioned vehicles for your perfect journey." },
    { icon: Headphones, title: "24/7 Support", description: "Always available for your needs, day or night, 365 days a year." },
    { icon: DollarSign, title: "Best Prices", description: "Competitive rates with no hidden fees. What you see is what you pay." },
    { icon: CheckCircle2, title: "Easy Booking", description: "Simple online reservation system with instant confirmation." },
    { icon: Shield, title: "Safety First", description: "Fully insured and certified for your peace of mind and safety." },
  ];

  if (slidesLoading) {
    return <Layout><div className="min-h-screen flex items-center justify-center"><div className="animate-spin-slow w-12 h-12 border-4 border-primary border-t-transparent rounded-full" /></div></Layout>;
  }

  return (
    <Layout>
      <div className="min-h-screen">
        {/* Hero Section - Full Width Background */}
        <section className="relative min-h-screen overflow-hidden">
          {/* Background Slides - Full Width */}
          {heroSlides?.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 bg-cover bg-center transition-all duration-1000 ${
                index === currentSlide ? 'opacity-100 scale-100' : 'opacity-0 scale-105'
              }`}
              style={{ backgroundImage: `url(${slide.image_url})` }}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/40" />

          {/* Slide Indicators */}
          {heroSlides && heroSlides.length > 1 && (
            <div className="absolute bottom-8 left-1/2 lg:left-8 lg:translate-x-0 -translate-x-1/2 z-20 flex space-x-2">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    index === currentSlide ? 'bg-secondary w-8' : 'bg-white/40 w-2 hover:bg-white/70'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Content Grid - Text + Form overlaid on full-width image */}
          <div className="relative z-10 container mx-auto px-4 min-h-screen flex items-center">
            <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 w-full py-24 lg:py-0">
              {/* Left - Text Content */}
              <div className="lg:col-span-3 flex flex-col justify-center">
                <Badge className="mb-6 bg-secondary hover:bg-secondary text-secondary-foreground border-0 text-sm px-5 py-1.5 rounded-full font-semibold w-fit">
                  ✈️ Best Airport Transportation
                </Badge>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black text-white mb-6 leading-[1.05] tracking-tight">
                  Your Reliable
                  <br />
                  Airport Transfers
                  <br />
                  <span className="text-secondary">in Punta Cana</span>
                </h1>
                
                <p className="text-lg md:text-xl text-white/75 max-w-lg font-light leading-relaxed mb-8">
                  Professional, comfortable, and on-time airport transportation across the Dominican Republic.
                </p>

                <div className="flex flex-wrap gap-6 text-white/60 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-secondary" />
                    <span>No Hidden Fees</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-secondary" />
                    <span>Flight Tracking</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-secondary" />
                    <span>24/7 Available</span>
                  </div>
                </div>
              </div>

              {/* Right - Floating Form Card */}
              <div className="hidden lg:flex lg:col-span-2 items-center justify-center">
                <div className="w-full bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6">
                  <TransferBookingForm hideResults onResultsFound={handleResultsFound} />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mobile Booking Form */}
        <section className="lg:hidden bg-muted/30 py-8 px-4">
          <div className="max-w-md mx-auto">
            <TransferBookingForm hideResults onResultsFound={handleResultsFound} />
          </div>
        </section>

        {/* Search Results Section */}
        {searchResults && (
          <section id="search-results" className="py-12 bg-muted/20">
            <div className="container mx-auto px-4 max-w-4xl">
              <TransferSearchResults
                selectedRoute={searchResults.selectedRoute}
                passengers={searchResults.passengers}
                tripType={searchResults.tripType}
                calculatePrice={searchResults.calculatePrice}
                fromName={searchResults.fromName}
                toName={searchResults.toName}
              />
            </div>
          </section>
        )}

        {/* How It Works Section */}
        <section className="py-24 bg-background relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-primary" />
          
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 text-sm px-6 py-2 rounded-full">How It Works</Badge>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">Book in <span className="text-secondary">3 Simple Steps</span></h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Getting your transfer has never been easier
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto relative">
              <div className="hidden md:block absolute top-16 left-[20%] right-[20%] h-0.5 bg-primary/20" />
              
              {steps.map((step, index) => (
                <div key={index} className="text-center relative animate-fade-in" style={{ animationDelay: `${index * 0.2}s` }}>
                  <div className="relative inline-flex items-center justify-center w-32 h-32 mb-6">
                    <div className="relative w-24 h-24 rounded-full bg-primary flex items-center justify-center shadow-lg">
                      <step.icon className="h-10 w-10 text-primary-foreground" />
                    </div>
                    <span className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-secondary text-secondary-foreground text-sm font-bold flex items-center justify-center shadow-lg">
                      {step.number}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                  <p className="text-muted-foreground text-sm">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Who We Are / Stats Section */}
        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="space-y-6 animate-fade-in">
                <Badge className="bg-secondary/10 text-secondary border-secondary/20 text-sm px-6 py-2 rounded-full">About Us</Badge>
                <h2 className="text-4xl md:text-5xl font-bold leading-tight">
                  {getConfig('home_about_title', 'The Smart Way to Travel')}
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {getConfig('home_about_description_1', 'Smart Punta Cana Transfer is your trusted airport transfer service in the Dominican Republic. We combine professionalism with local expertise to deliver seamless transportation experiences.')}
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  {getConfig('home_about_description_2', 'Our team of experienced, bilingual drivers ensure your journey is comfortable, safe, and on time. From airport pickups to hotel transfers, we\'re here to make your Dominican vacation exceptional.')}
                </p>
                <Link to="/about">
                  <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-full px-8 group mt-4">
                    Learn More About Us
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>

              <div className="grid grid-cols-2 gap-6 animate-scale-in">
                {[
                  { icon: Users, number: "500+", label: "Happy Customers" },
                  { icon: Award, number: "10+", label: "Years Experience" },
                  { icon: Headphones, number: "24/7", label: "Support Available" },
                  { icon: Shield, number: "100%", label: "Safe & Insured" },
                ].map((stat, index) => (
                  <Card key={index} className="text-center p-6 hover-lift border-0 shadow-md group">
                    <div className="w-14 h-14 bg-primary rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-lg">
                      <stat.icon className="h-7 w-7 text-primary-foreground" />
                    </div>
                    <h3 className="text-3xl font-black mb-1">{stat.number}</h3>
                    <p className="text-muted-foreground text-sm">{stat.label}</p>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 text-sm px-6 py-2 rounded-full">Our Advantages</Badge>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">Why Choose <span className="text-secondary">Smart Transfer</span></h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Experience the difference with professional airport transportation
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <Card key={index} className="p-8 hover-lift border-0 shadow-md group cursor-default">
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-all group-hover:scale-110">
                    <feature.icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed text-sm">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Airports Section */}
        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-secondary/10 text-secondary border-secondary/20 text-sm px-6 py-2 rounded-full">
                <Plane className="h-4 w-4 mr-1 inline" /> All Airports
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold mb-4">Dominican Republic <span className="text-secondary">Airports</span></h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Professional transfer service from and to all major airports
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {airports.map((airport) => (
                <Card key={airport.code} className={`p-6 hover-lift border-0 shadow-md relative overflow-hidden ${airport.popular ? 'ring-2 ring-secondary' : ''}`}>
                  {airport.popular && (
                    <Badge className="absolute top-4 right-4 bg-secondary text-secondary-foreground text-xs rounded-full">
                      Most Popular
                    </Badge>
                  )}
                  <div className="flex items-start space-x-4">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${
                      airport.popular ? 'bg-secondary' : 'bg-primary'
                    } shadow-lg`}>
                      <Plane className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Badge variant="outline" className="font-bold text-xs">{airport.code}</Badge>
                        <div className="flex items-center text-muted-foreground">
                          <Clock className="h-3 w-3 mr-1" />
                          <span className="text-xs">{airport.transferTime}</span>
                        </div>
                      </div>
                      <h3 className="text-sm font-bold mb-1">{airport.name}</h3>
                      <p className="text-xs text-muted-foreground">{airport.description}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <div className="mt-12 text-center">
              <Link to="/transfers">
                <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-full px-8 group">
                  Book Your Transfer Now
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        {featuredTestimonials.length > 0 && (
          <section className="py-24 bg-background">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16">
                <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 text-sm px-6 py-2 rounded-full">Client Reviews</Badge>
                <h2 className="text-4xl md:text-5xl font-bold mb-4">What Our <span className="text-secondary">Customers</span> Say</h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  Real experiences from travelers who trusted us
                </p>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {featuredTestimonials.map((testimonial) => (
                  <Card key={testimonial.id} className="p-8 hover-lift border-0 shadow-md relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1 bg-primary" />
                    <div className="flex items-center space-x-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          className={`h-5 w-5 ${i < testimonial.rating ? 'fill-secondary text-secondary' : 'text-muted'}`}
                        />
                      ))}
                    </div>
                    <p className="text-muted-foreground leading-relaxed mb-6 italic text-sm">
                      "{testimonial.comment}"
                    </p>
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                        <span className="text-primary-foreground font-bold text-sm">
                          {testimonial.customer_name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="font-bold text-sm">{testimonial.customer_name}</p>
                        <p className="text-xs text-muted-foreground">{testimonial.location}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ Section */}
        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-16">
                <Badge className="mb-4 bg-accent/10 text-accent border-accent/20 text-sm px-6 py-2 rounded-full">Help Center</Badge>
                <h2 className="text-4xl md:text-5xl font-bold mb-4">Frequently Asked <span className="text-primary">Questions</span></h2>
              </div>

              <Accordion type="single" collapsible className="space-y-3">
                {[
                  { q: "How do I book a transfer?", a: "Simply use our booking form, select your pickup and destination, choose the number of passengers, and click 'Search Available Transfers'. You can then select your preferred vehicle type and complete the booking online." },
                  { q: "What's included in the transfer price?", a: "All our transfers include door-to-door service, professional bilingual driver, meet & greet at the airport, flight tracking, all taxes, and tolls. There are no hidden fees." },
                  { q: "Do you offer child seats?", a: "Yes, we provide child seats and booster seats upon request at no additional charge. Please inform us when making your reservation." },
                  { q: "Can I cancel or modify my booking?", a: "Yes, you can cancel or modify your booking up to 24 hours before your scheduled pickup time for a full refund." },
                  { q: "What if my flight is delayed?", a: "We track all flights in real-time, so if your flight is delayed, we'll adjust your pickup time accordingly at no extra charge." },
                  { q: "Are there extra charges for luggage?", a: "No, standard luggage is included. Each passenger is allowed one large suitcase and one carry-on." },
                ].map((item, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="border-0 rounded-xl bg-background shadow-sm px-6 overflow-hidden">
                    <AccordionTrigger className="text-left hover:text-primary font-semibold hover:no-underline py-5">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-5">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              <div className="text-center mt-10">
                <p className="text-muted-foreground mb-4 text-sm">Still have questions?</p>
                <Link to="/contact">
                  <Button variant="outline" className="rounded-full group">
                    Contact Us
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="relative py-32 overflow-hidden bg-primary">
          <div className="relative z-10 container mx-auto px-4 text-center text-primary-foreground">
            <Zap className="h-14 w-14 mx-auto mb-6 text-secondary animate-float" />
            <h2 className="text-4xl md:text-6xl font-black mb-6">
              Ready to Book Your Transfer?
            </h2>
            <p className="text-xl mb-10 max-w-2xl mx-auto text-primary-foreground/80 font-light">
              Experience comfort and reliability with Smart Punta Cana Transfer.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Link to="/transfers">
                <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground text-lg px-10 py-6 h-auto rounded-full group font-bold">
                  Book Transfer Now
                  <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="bg-white/10 hover:bg-white/20 text-primary-foreground border-primary-foreground/20 text-lg px-10 py-6 h-auto rounded-full">
                  Contact Us
                </Button>
              </Link>
            </div>

            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center text-primary-foreground/70 text-sm">
              <div className="flex items-center space-x-2">
                <Phone className="h-4 w-4" />
                <span>{getConfig('contact_phone', '+1 (809) 123-4567')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4" />
                <span>{getConfig('contact_email', 'info@smartpuntacanatransfer.com')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Globe className="h-4 w-4" />
                <span>24/7 Available</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default HomePage;
